import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toastSuccess, toastError } from "@/lib/toast";

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type LeadRow = {
  id: number;
  fname?: string;
  sname?: string;
  phone_number?: string;
  town?: string;
  lead_status?: string;
  branch?: number | string;
  bdo?: number | string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  branches: Branch[];
  // ✅ remove global bdos list; load by selected branch
  initial?: LeadRow | null;
  onSaved: () => void;
};

export function LeadDialog({ open, onOpenChange, mode, branches, initial, onSaved }: Props) {
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string>("");

  const [branch, setBranch] = useState("");
  const [bdo, setBdo] = useState("");

  const [bdos, setBdos] = useState<Bdo[]>([]);
  const [bdosLoading, setBdosLoading] = useState(false);

  const [fname, setFname] = useState("");
  const [sname, setSname] = useState("");
  const [phone, setPhone] = useState("");
  const [town, setTown] = useState("");
  const [leadStatus, setLeadStatus] = useState("Hot");

  // prefill on open
  useEffect(() => {
    if (!open) return;

    setErr("");

    if (mode === "edit" && initial) {
      const initBranch = String((initial as any).branch ?? "");
      const initBdo = String((initial as any).bdo ?? "");

      setBranch(initBranch);
      setBdo(initBdo);

      setFname(initial.fname ?? "");
      setSname(initial.sname ?? "");
      setPhone(initial.phone_number ?? "");
      setTown(initial.town ?? "");
      setLeadStatus((initial.lead_status as string) ?? "Hot");
    } else {
      setBranch("");
      setBdo("");
      setBdos([]);
      setFname("");
      setSname("");
      setPhone("");
      setTown("");
      setLeadStatus("Hot");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial?.id]);

  // ✅ Branch-first BDO loading
  useEffect(() => {
    setErr("");
    setBdos([]);
    // always clear BDO when branch changes manually
    // (in edit mode we will restore if it exists after fetch)
    if (!branch) {
      setBdo("");
      return;
    }

    let alive = true;
    (async () => {
      setBdosLoading(true);
      try {
        const list = await apiFetch<Bdo[]>(`/actions/leads/bdos/${branch}`);
        if (!alive) return;

        const safeList = list ?? [];
        setBdos(safeList);

        // if current bdo not in list, clear it
        const exists = safeList.some((x) => String(x.id) === String(bdo));
        if (!exists) setBdo("");
      } catch (e: any) {
        if (!alive) return;
        setBdos([]);
        setBdo("");
        setErr(e?.message ?? "Failed to load BDOs");
      } finally {
        if (!alive) return;
        setBdosLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const title = mode === "create" ? "Create Lead" : "Edit Lead";

  async function submit() {
    setErr("");

    if (!branch) return setErr("Branch is required");
    if (!bdo) return setErr("BDO is required");
    if (!fname.trim()) return setErr("First name is required");
    if (!sname.trim()) return setErr("Last name is required");
    if (!phone.trim()) return setErr("Phone number is required");

    try {
      setSaving(true);

      const payload = {
        branch: Number(branch),
        bdo: Number(bdo),
        fname,
        sname,
        phone_number: phone,
        town,
        lead_status: leadStatus,
      };

      let res;

      if (mode === "create") {
        res = await apiFetch(`/actions/leads/create`, { method: "POST", body: JSON.stringify(payload) });
      } else {
        res = await apiFetch(`/actions/leads/update/${initial?.id}`, { method: "POST", body: JSON.stringify(payload) });
      }

      toastSuccess(res?.message || "Lead saved successfully");

      onOpenChange(false);
      onSaved();
    } catch (e: any) {
      toastError(e?.message || "Failed to save lead");
    } finally {
      setSaving(false);
    }

  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={[
          "[&>button]:hidden",
          "w-[100vw] h-[100dvh] max-w-none rounded-none p-0",
          "sm:rounded-2xl sm:h-auto sm:max-h-[90dvh] sm:w-[92vw] sm:max-w-[820px] sm:p-0",
          isRTL ? "sm:ml-6 sm:mr-auto" : "sm:mr-6 sm:ml-auto",
        ].join(" ")}
      >
        <div className="flex flex-col h-full sm:h-auto">
          <DialogHeader className="px-4 py-3 border-b border-border flex-row items-center justify-between">
            <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>

          <div className="p-4">
            {/* ✅ 2 columns on tablet/desktop, 1 column on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-sm font-medium">
                  Branch <span className="text-destructive">*</span>
                </div>
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select branch</option>
                  {branches.map((b) => (
                    <option key={b.id} value={String(b.id)}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium">Phone Number</div>
                {/* <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone Number" className="h-11" />
               */}
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  readOnly={mode === "edit"}
                  placeholder="Phone Number"
                  className={cn(
                    "h-11",
                    mode === "edit" && "bg-muted cursor-not-allowed"
                  )}
                />
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium">
                  BDO <span className="text-destructive">*</span>
                </div>
                <select
                  value={bdo}
                  onChange={(e) => setBdo(e.target.value)}
                  disabled={!branch || bdosLoading}
                  className={cn(
                    "h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                    (!branch || bdosLoading) && "opacity-60 cursor-not-allowed"
                  )}
                >
                  <option value="">
                    {!branch ? "Select branch first" : bdosLoading ? "Loading BDOs..." : "Select BDO"}
                  </option>
                  {bdos.map((x) => (
                    <option key={x.id} value={String(x.id)}>
                      {x.name}
                    </option>
                  ))}
                </select>

                {bdosLoading && (
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-2 pt-1">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading BDOs...
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium">Town</div>
                <Input value={town} onChange={(e) => setTown(e.target.value)} placeholder="Town" className="h-11" />
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium">First Name</div>
                <Input value={fname} onChange={(e) => setFname(e.target.value)} placeholder="First Name" className="h-11" />
              </div>

              <div className="space-y-1">
                <div className="text-sm font-medium">Status</div>
                <select
                  value={leadStatus}
                  onChange={(e) => setLeadStatus(e.target.value)}
                  className="h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="Cold">Cold</option>
                  <option value="Warm">Warm</option>
                  <option value="Hot">Hot</option>
                </select>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <div className="text-sm font-medium">Last Name</div>
                <Input value={sname} onChange={(e) => setSname(e.target.value)} placeholder="Last Name" className="h-11" />
              </div>
            </div>

            {err ? <div className="mt-3 text-sm text-destructive">{err}</div> : null}

            {/* ✅ footer buttons: stack on mobile */}
            
          </div>
        </div>
         <div className="border-t border-border px-5 py-4">
                  <DialogFooter className="gap-2 sm:gap-3">
                    <Button variant="secondary" className="h-11" onClick={() => onOpenChange(false)} disabled={saving}>
                Close
              </Button>

              <Button className="h-11" onClick={submit} disabled={saving || bdosLoading}>
                {saving ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : mode === "create" ? (
                  "Create"
                ) : (
                  "Update"
                )}
              </Button>
                    
                  </DialogFooter>
                </div>
      </DialogContent>
    </Dialog>
  );
}